import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useBreadcrumb } from './BreadcrumbContext';
import { ROUTE_LABEL_MAP } from '../routes/AppRoutes';
import { resolveCounsellingTitle } from '../pages/Counselling/conduct-counselling/pageTitle';
import { setRowData } from '../store/slices/counsellingSlice';

/**
 * Segments that are just URL prefixes with no corresponding page.
 * These are skipped in the breadcrumb.
 */
const PREFIX_SEGMENTS = new Set(['counselling']);

function getLabel(segment: string): string {
  return (
    ROUTE_LABEL_MAP[segment] ||
    segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const Breadcrumb = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { subItems } = useBreadcrumb();
  const dispatch = useDispatch();

  // Clicking any breadcrumb crumb clears the order-copy hand-off row, then runs
  // the crumb's own onClick (if any).
  const handleCrumbClick = (onClick?: () => void) => () => {
    dispatch(setRowData(''));
    onClick?.();
  };

  const pathSegments = location.pathname
    .split('/')
    .filter((s) => s && !PREFIX_SEGMENTS.has(s));

  // The DEE page ('/counselling/dee') shows a title based on its ?path= query
  // (Check Candidate List, Check Vacancy, etc.) rather than the generic label.
  const isDeePage = location.pathname === '/counselling/dee';
  const deeTitle = isDeePage
    ? resolveCounsellingTitle(searchParams.get('path') || '')
    : '';

  // The order-copy-details page supplies its whole crumb chain (role-based
  // parent + counselling Title) via context sub-items — render Home + those.
  const isOrderCopyPage =
    location.pathname === '/counselling/order-copy-details';

  // The seniority-order page likewise owns its full chain
  // (Seniority Update / <Title> / Seniority Order) via context sub-items.
  const isSeniorityOrderPage =
    location.pathname === '/counselling/seniority-order';

  // The seniority upload-type hub owns its chain (Update Seniority / <Title>).
  // Without this, the route's own "Update Seniority" label and the page's
  // "Update Seniority" sub-item would both render (duplicate crumb).
  const isSeniorityTypePage =
    location.pathname === '/counselling/seniority-type';

  // The seniority excel-upload page owns its chain
  // (Update Seniority / <Title> / Excel Uploaded Seniority) via sub-items.
  const isSeniorityExcelPage =
    location.pathname === '/counselling/seniority-excel-upload';

  const ownsCrumbChain =
    isOrderCopyPage ||
    isSeniorityOrderPage ||
    isSeniorityTypePage ||
    isSeniorityExcelPage;

  if (pathSegments.length === 0) return null;

  const hasSubItems = subItems.length > 0;

  return (
    <nav aria-label="breadcrumb" className="app-breadcrumb">
      <ol className="breadcrumb">
        <li className="breadcrumb-item">
          <Link
            className="breadcrumb-link"
            to="/counselling/dashboard"
            onClick={handleCrumbClick()}
          >
            <i className="pi pi-home" />
          </Link>
        </li>

        {/* DEE page: HomeIcon / Counselling / <title> / <list-or-detail> */}
        {isDeePage ? (
          <>
            <li className="breadcrumb-item">
              <Link
                className="breadcrumb-link"
                to="/counselling/front-page"
                onClick={handleCrumbClick()}
              >
                Counselling
              </Link>
            </li>
            {hasSubItems ? (
              <li className="breadcrumb-item">
                <span
                  className="breadcrumb-link"
                  onClick={handleCrumbClick(subItems[0].onClick)}
                >
                  {deeTitle}
                </span>
              </li>
            ) : (
              <li className="breadcrumb-item active" aria-current="page">
                {deeTitle}
              </li>
            )}
          </>
        ) : ownsCrumbChain ? (
          /* Page supplies its entire chain via context sub-items. */
          <></>
        ) : (
          pathSegments.map((segment, index) => {
            const path = '/' + pathSegments.slice(0, index + 1).join('/');
            const isLast = index === pathSegments.length - 1;
            const label =
              segment === 'dee' && deeTitle ? deeTitle : getLabel(segment);

            // If last segment but there are sub-items, make it clickable
            if (isLast && hasSubItems) {
              const firstSub = subItems[0];
              return (
                <li key={path} className="breadcrumb-item">
                  <span
                    className="breadcrumb-link"
                    onClick={handleCrumbClick(firstSub.onClick)}
                  >
                    {label}
                  </span>
                </li>
              );
            }

            return isLast ? (
              <li
                key={path}
                className="breadcrumb-item active"
                aria-current="page"
              >
                {label}
              </li>
            ) : (
              <li key={path} className="breadcrumb-item">
                <Link
                  className="breadcrumb-link"
                  to={path}
                  onClick={handleCrumbClick()}
                >
                  {label}
                </Link>
              </li>
            );
          })
        )}
        {/* Sub-items from context */}
        {subItems.map((item, index) => {
          const isLast = index === subItems.length - 1;
          return isLast ? (
            <li
              key={item.label}
              className="breadcrumb-item active"
              aria-current="page"
            >
              {item.label}
            </li>
          ) : (
            <li key={item.label} className="breadcrumb-item">
              <span
                className="breadcrumb-link"
                onClick={handleCrumbClick(item.onClick)}
              >
                {item.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
